import React, {
  FC,
  HTMLAttributes,
  MouseEvent,
  ReactNode,
  useEffect,
  useState,
} from "react";
import styled, { useTheme } from "styled-components";
import Button from "../Button";
import { translate } from "pdf-lib";
import { useTranslation } from "_metronic/i18n/language";
import { Image } from "@mui/icons-material";
import { backgroundClip } from "html2canvas/dist/types/css/property-descriptors/background-clip";
import CardProgress from "./CardProgress";

export interface PageInfo {
  title?: string;
  chiildren: ReactNode;
  functionNextButton?: () => any;
}

interface PagesStepProps extends HTMLAttributes<HTMLDivElement> {
  pagesInfo: PageInfo[];
  initialStage?: number;
  finalButtonContent?: ReactNode;
  nextButtonShow?: number[] | boolean;
  previousButtonShow?: number[] | boolean;
  progressStageShow?: boolean;
}

interface ElementProps {
  children: ReactNode;
  idPage: number;
}

const Container = styled.div`
  min-height: 500px;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const ButtonsStep = styled.div`
  display: flex;
  margin-left: auto;
  gap: 15px;
  padding: 1em;
`;

const ActionsContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
`;
const ProgressContainer = styled.div`
  display: flex;
  flex-grow: 1;
`;

const PagesStep: FC<PagesStepProps> = ({
  pagesInfo,
  initialStage = 0,
  finalButtonContent,
  nextButtonShow = true,
  previousButtonShow = true,
  progressStageShow = false,
}) => {
  const [pages, setPages] = useState<ElementProps[]>([]);
  const [steps, setSteps] = useState<number>(0);
  const [currentstep, setCurrentStep] = useState<number>(0);

  const theme = useTheme();

  useEffect(() => {
    setSteps(pagesInfo.length - 1);
    setPages(
      pagesInfo.map((pageInfo, index) => ({
        children: pageInfo.chiildren,
        idPage: index,
      })),
    );

    setCurrentStep(initialStage);
  }, [pagesInfo]);

  const handleStep = async (
    e: MouseEvent<HTMLButtonElement>,
    action: boolean,
  ) => {
    e.preventDefault();

    if (action) {
      const pageStage: PageInfo = pagesInfo[currentstep];

      if (pageStage.functionNextButton) {
        const functionSuccess = await pageStage.functionNextButton();

        if (functionSuccess) {
          setCurrentStep(prev => (prev < steps ? prev + 1 : prev));
        }
      } else {
        setCurrentStep(prev => (prev < steps ? prev + 1 : prev));
      }
    } else {
      setCurrentStep(prev => (prev > 0 ? prev - 1 : prev));
    }
  };

  const translate = useTranslation();

  return (
    <Container>
      {pages.map(page => currentstep === page.idPage && page.children)}

      <ActionsContainer>
        {progressStageShow && (
          <ProgressContainer>
            {pagesInfo.length > 0 &&
              pagesInfo.map((pageInfo, index) => (
                <CardProgress
                  key={index}
                  title={pageInfo.title || ""}
                  namberPage={index + 1}
                  showAfter={index < pagesInfo.length - 1}
                  active={index < currentstep}
                  height="120px"
                  width="200px"
                />
              ))}
          </ProgressContainer>
        )}
        <ButtonsStep>
          {previousButtonShow &&
            (previousButtonShow === true ||
              (Array.isArray(previousButtonShow) &&
                previousButtonShow.includes(currentstep))) &&
            currentstep > 0 && (
              <Button
                style={{
                  backgroundColor: theme.error,
                  color: "#FFF",
                }}
                styleHover={{
                  backgroundColor: "transparent",
                  color: theme.error,
                }}
                onClick={e => handleStep(e, false)}
              >
                {translate("datatable_backButton")}
              </Button>
            )}
          {nextButtonShow &&
            (nextButtonShow === true ||
              (Array.isArray(nextButtonShow) &&
                nextButtonShow.includes(currentstep))) &&
            currentstep < steps && (
              <Button
                style={{
                  backgroundColor: theme.success,
                  color: "#FFF",
                }}
                styleHover={{
                  backgroundColor: "transparent",
                  color: theme.success,
                }}
                onClick={e => handleStep(e, true)}
              >
                {translate("buttons_next")}
              </Button>
            )}
          {currentstep === steps && finalButtonContent && finalButtonContent}
        </ButtonsStep>
      </ActionsContainer>
    </Container>
  );
};

export default PagesStep;
